import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '@frontend/context/authContext';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@frontend/shadcn/components/ui/card';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Label } from '@frontend/shadcn/components/ui/label';
import { Textarea } from '@frontend/shadcn/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@frontend/shadcn/components/ui/radio-group';
import { Calendar } from '@frontend/shadcn/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { toast } from '@frontend/shadcn/hooks/use-toast';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@frontend/shadcn/components/ui/form';
import { CalendarIcon, Info } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@frontend/shadcn/lib/utils';

const formSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  companyName: z.string().min(1, 'Please enter the company name'),
  brandName: z.string().min(1, 'Please enter the brand name'),
  billingAddressChanged: z.string().min(1, 'Please specify if billing address changed'),
  invoiceAmount: z.number().min(0.01, 'Please enter a valid amount'),
  invoiceFrequency: z.string().min(1, 'Please select invoice frequency'),
  otherInvoiceFrequency: z.string().optional(),
  recurringEndDate: z.date().optional(),
  clientId: z.string().min(1, 'Please provide the client ID'),
  poNumber: z.string().min(1, 'Please select PO option'),
  otherPoNumber: z.string().optional(),
  preferredInvoiceDate: z.date().optional(),
  netTerms: z.string().min(1, 'Please select net terms'),
  otherNetTerms: z.string().optional(),
  invoiceRecipients: z.string().min(1, 'Please select invoice recipients'),
  otherInvoiceRecipients: z.string().optional(),
  comments: z.string().optional(),
});

type InvoiceRequest = z.infer<typeof formSchema>;

const RequestInvoiceSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { user, clientInfo } = useAuth();

  const invoiceFrequencyOptions = ['One-Time', 'Recurring Monthly'];

  const netTermsOptions = ['Default - Upon Receipt', 'N15', 'N30', 'N45'];

  const invoiceRecipientsOptions = ["Brand's billing contacts on file"];

  const poNumberOptions = ['PO not needed'];

  const form = useForm<InvoiceRequest>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: user?.email || '',
      invoiceFrequency: 'One-Time',
      otherInvoiceFrequency: '',
      netTerms: '',
      otherNetTerms: '',
      invoiceRecipients: '',
      otherInvoiceRecipients: '',
      billingAddressChanged: 'no',
      poNumber: '',
      otherPoNumber: '',
      clientId: clientInfo?.id || '',
      companyName: clientInfo?.name || '',
      brandName: clientInfo?.name || '',
    },
  });

  const handleSubmit = async (values: InvoiceRequest) => {
    setLoading(true);
    try {
      // Create URL-encoded form data matching the HTML form structure
      const formParams = new URLSearchParams();

      // Email address (Google Forms may collect this automatically or through emailAddress field)
      formParams.append('emailAddress', values.email);

      // Basic form fields
      formParams.append('entry.179000191', values.companyName); // Company name
      formParams.append('entry.1625156957', values.brandName); // Brand name
      formParams.append('entry.1823862996', values.billingAddressChanged); // Billing address changed
      formParams.append('entry.795637794', values.invoiceAmount.toString()); // Invoice amount

      // Invoice Frequency - handle "Other" option
      if (values.invoiceFrequency === 'Other') {
        formParams.append('entry.839245129', '__other_option__');
        formParams.append('entry.839245129.other_option_response', values.otherInvoiceFrequency || '');
      } else {
        formParams.append('entry.839245129', values.invoiceFrequency);
      }

      // Date fields - use standard entry format (not _date suffix)
      if (values.recurringEndDate) {
        formParams.append('entry.1393360613', values.recurringEndDate.toISOString().split('T')[0]);
      }

      formParams.append('entry.1007256092', values.clientId); // Client ID

      // PO Number - handle "Other" option
      if (values.poNumber === 'Other') {
        formParams.append('entry.629160111', '__other_option__');
        formParams.append('entry.629160111.other_option_response', values.otherPoNumber || '');
      } else {
        formParams.append('entry.629160111', values.poNumber);
      }

      // Preferred invoice date
      if (values.preferredInvoiceDate) {
        formParams.append('entry.208358970', values.preferredInvoiceDate.toISOString().split('T')[0]);
      }

      // Net Terms - checkbox field, handle "Other" option
      if (values.netTerms === 'Other') {
        formParams.append('entry.291865712', '__other_option__');
        formParams.append('entry.291865712.other_option_response', values.otherNetTerms || '');
      } else {
        formParams.append('entry.291865712', values.netTerms);
      }

      // Invoice Recipients - checkbox field, handle "Other" option
      if (values.invoiceRecipients === 'Other') {
        formParams.append('entry.684371666', '__other_option__');
        formParams.append('entry.684371666.other_option_response', values.otherInvoiceRecipients || '');
      } else {
        formParams.append('entry.684371666', values.invoiceRecipients);
      }

      // Comments
      formParams.append('entry.1946286543', values.comments || '');

      // Hidden fields matching the HTML form
      formParams.append('fvv', '1');
      formParams.append('fbzx', '-1492148283620721600'); // Updated fbzx value from HTML
      formParams.append('pageHistory', '0,1,2,3,4');

      // Submit to Google Form
      await fetch(
        'https://docs.google.com/forms/u/0/d/e/1FAIpQLSfZrAnX_t-6A_O6kKHfQs7uXKN0m7HDFDwNYxJUvyJgegxKMw/formResponse',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formParams.toString(),
          mode: 'no-cors', // Required for Google Forms submission
        },
      );

      // Since we're using no-cors mode, we can't check the response status
      // Google Forms will redirect on success, so if we reach here without error, it likely succeeded
      setIsSubmitted(true);
      form.reset();
    } catch (error) {
      console.error('Error submitting invoice request:', error);

      // More detailed error logging
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        // This might actually be a success due to CORS redirect
        setIsSubmitted(true);
        form.reset();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to submit invoice request. Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnother = () => {
    setIsSubmitted(false);
    form.reset({
      email: user?.email || '',
      invoiceFrequency: 'One-Time',
      otherInvoiceFrequency: '',
      netTerms: '',
      otherNetTerms: '',
      invoiceRecipients: '',
      otherInvoiceRecipients: '',
      billingAddressChanged: 'no',
      poNumber: '',
      otherPoNumber: '',
      clientId: clientInfo?.id || '',
      companyName: clientInfo?.name || '',
      brandName: clientInfo?.name || '',
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Request Invoice</h1>
        <p className="text-gray-600">
          Submit invoice requests for account funding. All requests will be reviewed by the finance team.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Deposit Creator Fund Balance</CardTitle>
          <CardDescription>
            Use this form to request an invoice for creator funds. Once the invoice has been paid, the funds will be
            added to your platform balance and you can pay your creators. Payments can be made by ACH, Wire, or Check.
            ACH is the preferred method and allows for the fastest processing times.
            <br />
            <br />
            Bank fees for wire transfers (approximately $25-$45) are not covered by Aspire and the net amount received
            will be what is posted to your account. Additional handling fees may apply.
            <br />
            <br />
            If you have any questions, please email{' '}
            <a href="mailto:AR@aspireiq.com" className="text-blue-600 hover:text-blue-800 underline">
              AR@aspireiq.com
            </a>
            .
            <Separator className="my-4" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isSubmitted ? (
            <div className="text-center py-12">
              <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Invoice Request Submitted Successfully!</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Your invoice request has been submitted and is being processed. Please allow 48 hours for our finance
                team to generate and send the requested invoice.
              </p>
              <div className="space-y-3">
                <Button onClick={handleSubmitAnother} className="px-6">
                  Submit Another Request
                </Button>
                <p className="text-sm text-gray-500">
                  You will receive an email confirmation once the invoice has been generated.
                </p>
              </div>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                {/* Company Information Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Company Information</h3>

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="your.email@company.com" {...field} />
                        </FormControl>
                        <FormDescription>This email will be used for invoice communications.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>What is the name of the company this invoice should be addressed to?</FormLabel>
                        <FormControl>
                          <Input placeholder="Company Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="brandName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>What is the Brand name in which the funds should be deposited to?</FormLabel>
                        <FormControl>
                          <Input placeholder="Brand Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="billingAddressChanged"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Has your billing address changed? If so, please enter the new address below:
                        </FormLabel>
                        <FormControl>
                          <Textarea placeholder="Enter new address if changed, otherwise type 'no'" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Balance Details Section */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold">Balance Details</h3>
                    <p className="text-sm text-gray-600">
                      Let us know how much balance you would like to add to your account.
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="invoiceAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Please confirm the total amount that should be invoiced (in USD)
                          <br />
                          <span className="text-sm text-gray-600">
                            Note: This amount should represent what you want to add to your platform balance (does not
                            include fees).
                          </span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="invoiceFrequency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Invoice Frequency</FormLabel>
                        <FormControl>
                          <RadioGroup onValueChange={field.onChange} value={field.value} className="space-y-3">
                            {invoiceFrequencyOptions.map((option) => (
                              <div key={option} className="flex items-center space-x-2">
                                <RadioGroupItem value={option} id={`invoiceFrequency-${option}`} />
                                <Label htmlFor={`invoiceFrequency-${option}`} className="text-sm font-normal">
                                  {option}
                                </Label>
                              </div>
                            ))}
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="Other" id="invoiceFrequency-Other" />
                              <Label htmlFor="invoiceFrequency-Other" className="text-sm font-normal">
                                Other:
                              </Label>
                              {field.value === 'Other' && (
                                <FormField
                                  control={form.control}
                                  name="otherInvoiceFrequency"
                                  render={({ field: otherField }) => (
                                    <FormItem className="ml-2 flex-1">
                                      <FormControl>
                                        <Input placeholder="Specify other frequency" {...otherField} className="h-8" />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />
                              )}
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="recurringEndDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          If Recurring invoice frequency, indicate last date of invoice. If left blank, 1 month prior to
                          subscription end date will be used.
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground',
                                )}
                              >
                                {field.value ? format(field.value, 'PPP') : <span>Pick a date (optional)</span>}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => date < new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Invoice Details Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Invoice Details</h3>

                  <FormField
                    control={form.control}
                    name="clientId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client ID</FormLabel>
                        <FormControl>
                          <Input placeholder="Client ID" {...field} readOnly className="bg-gray-50 text-gray-600" />
                        </FormControl>
                        <FormDescription>This is automatically populated from your account settings.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="poNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Please enter PO number if applicable <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <RadioGroup onValueChange={field.onChange} value={field.value} className="space-y-3">
                            {poNumberOptions.map((option) => (
                              <div key={option} className="flex items-center space-x-2">
                                <RadioGroupItem value={option} id={`poNumber-${option}`} />
                                <Label htmlFor={`poNumber-${option}`} className="text-sm font-normal">
                                  {option}
                                </Label>
                              </div>
                            ))}
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="Other" id="poNumber-Other" />
                              <Label htmlFor="poNumber-Other" className="text-sm font-normal">
                                Other:
                              </Label>
                              {field.value === 'Other' && (
                                <FormField
                                  control={form.control}
                                  name="otherPoNumber"
                                  render={({ field: otherField }) => (
                                    <FormItem className="ml-2 flex-1">
                                      <FormControl>
                                        <Input placeholder="Enter PO number" {...otherField} className="h-8" />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />
                              )}
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="preferredInvoiceDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Preferred Invoice Date? If left blank, the next available business day will be used
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal',
                                  !field.value && 'text-muted-foreground',
                                )}
                              >
                                {field.value ? format(field.value, 'PPP') : <span>Pick a date (optional)</span>}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => date < new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="netTerms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Preferred Net Terms (Upon Receipt is defaulted) <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <RadioGroup onValueChange={field.onChange} value={field.value} className="space-y-3">
                            {netTermsOptions.map((option) => (
                              <div key={option} className="flex items-center space-x-2">
                                <RadioGroupItem value={option} id={`netTerms-${option}`} />
                                <Label htmlFor={`netTerms-${option}`} className="text-sm font-normal">
                                  {option}
                                </Label>
                              </div>
                            ))}
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="Other" id="netTerms-Other" />
                              <Label htmlFor="netTerms-Other" className="text-sm font-normal">
                                Other:
                              </Label>
                              {field.value === 'Other' && (
                                <FormField
                                  control={form.control}
                                  name="otherNetTerms"
                                  render={({ field: otherField }) => (
                                    <FormItem className="ml-2 flex-1">
                                      <FormControl>
                                        <Input placeholder="Specify other net terms" {...otherField} className="h-8" />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />
                              )}
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="invoiceRecipients"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Invoice Recipients <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <RadioGroup onValueChange={field.onChange} value={field.value} className="space-y-3">
                            {invoiceRecipientsOptions.map((option) => (
                              <div key={option} className="flex items-center space-x-2">
                                <RadioGroupItem value={option} id={`invoiceRecipients-${option}`} />
                                <Label htmlFor={`invoiceRecipients-${option}`} className="text-sm font-normal">
                                  {option}
                                </Label>
                              </div>
                            ))}
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="Other" id="invoiceRecipients-Other" />
                              <Label htmlFor="invoiceRecipients-Other" className="text-sm font-normal">
                                Other:
                              </Label>
                              {field.value === 'Other' && (
                                <FormField
                                  control={form.control}
                                  name="otherInvoiceRecipients"
                                  render={({ field: otherField }) => (
                                    <FormItem className="ml-2 flex-1">
                                      <FormControl>
                                        <Input placeholder="Specify other recipients" {...otherField} className="h-8" />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />
                              )}
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Comments Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Comments</h3>

                  <FormField
                    control={form.control}
                    name="comments"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Is there anything else we should know prior to generating the invoice?</FormLabel>
                        <FormControl>
                          <Textarea rows={4} placeholder="Additional comments or requirements..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex justify-end pt-6">
                  <Button type="submit" disabled={loading} className="px-8">
                    {loading ? 'Submitting...' : 'Submit Invoice Request'}
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Info className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-800">Important Information</h3>
              <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
                <li>ACH is the preferred payment method and allows for the fastest processing times</li>
                <li>Bank fees for wire transfers (approximately $25-$45) are not covered by Aspire</li>
                <li>The net amount received will be what is posted to your account</li>
                <li>Additional handling fees may apply</li>
                <li>
                  For questions, please email{' '}
                  <a href="mailto:AR@aspireiq.com" className="text-blue-600 hover:text-blue-800 underline">
                    AR@aspireiq.com
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RequestInvoiceSettings;
